import { Nullable } from 'global';
import { moneyService } from 'shared';
import { v4 as uuidv4 } from 'uuid';
import { formatStringToNumber } from 'utils/formatters';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import { DiscountType, QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { UpdateInvoiceVariables } from 'modules/invoice/api/types';
import {
  ChargeAttachType,
  ChargeParentKind,
  Charge,
  Equipment,
  BasePrice,
  ChargeBucketRef,
  ChargesByType,
} from 'modules/equipment/types';

import { equipmentCalc, ChargeListingService } from '../lib';
import {
  calcFees,
  calcTaxes,
  calculateTotal,
  ChargeDefinition,
  ChargeType,
  chargeTypesByLabels,
  newChargeRow,
} from '../../equipment';
import {
  Quote,
  QuoteInput,
  EquipmentLike,
  EquipmentSyncDTO,
  QuoteLineItemType,
  QuoteEquipmentEntry,
  QuoteChargeListingEntity,
  QuoteEquipmentWithCharge,
} from '../types';

// Quote attribute marking that an equipment's options were already applied, so the quote fees become the source of truth
const EQUIPMENT_OPTIONS_APPLIED_KEY_PREFIX = 'equipmentOptionsApplied:';

class QuoteMapper {
  private readonly chargeListingService = new ChargeListingService();

  public toListingRow(equipment: Record<string, any>, quote: Nullable<Quote>): QuoteChargeListingEntity {
    const priceFromQuote = (quote?.equipment ?? []).find(
      quoteEquipment => quoteEquipment.equipmentId === equipment.equipmentId
    )?.price;

    return {
      charge: null,
      type: QuoteLineItemType.Equipment,
      equipment: {
        ...equipment,
        price: priceFromQuote
          ? {
              ...priceFromQuote,
              wholesalePrice: equipment.wholesaleCost ?? priceFromQuote.wholesalePrice,
              retailPrice: equipment.retailPrice ?? priceFromQuote.retailPrice,
            }
          : this.toNewEquipmentPrice(equipment.wholesaleCost ?? 0, equipment.retailPrice ?? 0),
        attachments: (equipment.attachments ?? []).map((attachment: any) => {
          const wholesalePrice = formatStringToNumber(attachment.unitCost) ?? 0;
          const retailPrice = attachment.retailPrice ?? 0;

          return {
            ...attachment,
            price: {
              ...newChargeRow,
              retailPrice,
              wholesalePrice,
              markupType: DiscountType.Flat,
              markup: this.calcMarkup(wholesalePrice, retailPrice, DiscountType.Flat),
              discount: attachment.discountValue ?? 0,
              discountType: attachment.discountType ?? DiscountType.Flat,
            },
          };
        }),
      },
    };
  }

  // Retail price entered in the equipment dialog is the source of truth, so markup is derived from it
  public toUpdatedListingRow(
    currentRow: Nullable<QuoteChargeListingEntity>,
    equipment: Record<string, any>
  ): QuoteChargeListingEntity {
    const currentEquipment = currentRow?.equipment ?? null;
    const { equipment: mappedEquipment } = this.toListingRow(equipment, null);
    const currentPrice = currentEquipment?.price ?? mappedEquipment!.price;

    const wholesalePrice = equipment.wholesaleCost ?? currentPrice.wholesalePrice ?? 0;
    const retailPrice = equipment.retailPrice ?? currentPrice.retailPrice ?? 0;
    const markupType = currentPrice.markupType ?? DiscountType.Flat;
    const markup = this.calcMarkup(wholesalePrice, retailPrice, markupType);

    const attachments = mappedEquipment!.attachments.map((attachment, index) => {
      const attachmentMarkupType = currentEquipment?.attachments?.[index]?.price?.markupType ?? DiscountType.Flat;

      return {
        ...attachment,
        price: {
          ...attachment.price,
          markupType: attachmentMarkupType,
          markup: this.calcMarkup(attachment.price.wholesalePrice, attachment.price.retailPrice, attachmentMarkupType),
        },
      };
    });

    return {
      charge: null,
      type: QuoteLineItemType.Equipment,
      equipment: {
        ...currentEquipment,
        ...mappedEquipment!,
        attachments,
        price: { ...currentPrice, wholesalePrice, retailPrice, markup, markupType },
      },
    };
  }

  // The server computes sell price as wholesale + markup, so a flat markup keeps the equipment's retail price exact
  private toNewEquipmentPrice(wholesalePrice: number, retailPrice: number): BasePrice {
    if (retailPrice <= 0) return { ...newChargeRow, wholesalePrice, retailPrice };

    return {
      ...newChargeRow,
      wholesalePrice,
      retailPrice,
      markupType: DiscountType.Flat,
      markup: this.calcMarkup(wholesalePrice, retailPrice, DiscountType.Flat),
    };
  }

  private calcMarkup(wholesalePrice: number, retailPrice: number, markupType: DiscountType): number {
    const markupAmount = retailPrice - wholesalePrice;

    if (markupType !== DiscountType.Percent) return moneyService.roundToCents(markupAmount);

    return wholesalePrice > 0 ? moneyService.roundToCents((markupAmount / wholesalePrice) * 100) : 0;
  }

  private calcRetailPrice({ wholesalePrice = 0, markup = 0, markupType, retailPrice = 0 }: BasePrice): number {
    const markupAmount = markupType === DiscountType.Percent ? (wholesalePrice * markup) / 100 : markup;

    return moneyService.roundToCents(wholesalePrice + markupAmount) || retailPrice;
  }

  private toAttachmentSyncDTO(attachment: QuoteEquipmentWithCharge['attachments'][number]) {
    const { price } = attachment;

    return {
      type: attachment.type,
      model: attachment.model,
      category: attachment.category,
      manufacturer: attachment.manufacturer,
      quantityPerAsset: attachment.quantityPerAsset,
      unitCost: price.wholesalePrice ?? 0,
      retailPrice: this.calcRetailPrice(price),
      discountValue: price.discount ?? 0,
      discountType: price.discountType ?? DiscountType.Flat,
    };
  }

  public toListingRows(voId: string, list: Equipment[], quote: Nullable<Quote>): QuoteChargeListingEntity[] {
    if (!Array.isArray(list) || !list.length) return [];

    const optionNamesByEquipmentId = this.buildOptionNameLookup(list);
    const equipmentRows = list.map(entity => this.toListingRow(entity, quote));
    const parentKindById = this.buildParentKindLookup(equipmentRows);
    const feeRows = (quote?.fees ?? []).map(fee =>
      this.toFeeListingRow(fee, voId, parentKindById, optionNamesByEquipmentId)
    );

    // Auto-apply matching equipment options only until they have been applied once; afterwards quote fees are authoritative
    const optionRows = list
      .filter(entity => !this.hasAppliedEquipmentOptions(quote, entity.equipmentId))
      .flatMap(entity => this.buildMissingEquipmentOptionRows(entity, quote));

    return this.chargeListingService
      .buildBuckets([...equipmentRows, ...feeRows, ...optionRows], voId)
      .flatMap(bucket => bucket.data);
  }

  public hasAppliedEquipmentOptions(quote: Nullable<Quote>, equipmentId: string): boolean {
    const key = `${EQUIPMENT_OPTIONS_APPLIED_KEY_PREFIX}${equipmentId}`;

    return (quote?.attributes ?? []).some(attribute => attribute.key === key);
  }

  private optionNamesOf(equipment: Equipment): Set<string> {
    return new Set((equipment.options ?? []).map(option => option.name));
  }

  private savedOptionLabels(quote: Nullable<Quote>, equipment: Equipment): Set<string> {
    const optionNames = this.optionNamesOf(equipment);

    return new Set(
      (quote?.fees ?? [])
        .filter(fee => fee.equipmentId === equipment.equipmentId && optionNames.has(fee.description))
        .map(fee => fee.description)
    );
  }

  private currentOptionLabels(rows: QuoteChargeListingEntity[], equipment: Equipment): Set<string> {
    const optionNames = this.optionNamesOf(equipment);

    return new Set(
      rows
        .filter(
          ({ type, charge }) =>
            type === QuoteLineItemType.Invoice &&
            charge?.groupBucket?.id === equipment.equipmentId &&
            (charge?.isEquipmentOption || optionNames.has(charge?.label ?? ''))
        )
        .map(row => row.charge!.label)
    );
  }

  // Keeps existing quote attributes and records that each equipment with options has been applied
  public toQuoteAttributesWithAppliedOptions(quote: Nullable<Quote>, equipmentList: Equipment[]): Quote['attributes'] {
    const existing = (quote?.attributes ?? []).map(({ key, value, description }) => ({ key, value, description }));

    const markers = equipmentList
      .filter(({ equipmentId, options }) => options?.length && !this.hasAppliedEquipmentOptions(quote, equipmentId))
      .map(({ equipmentId }) => ({
        value: 'true',
        description: 'Equipment options applied',
        key: `${EQUIPMENT_OPTIONS_APPLIED_KEY_PREFIX}${equipmentId}`,
      }));

    return [...existing, ...markers];
  }

  // True when the options shown in the listing no longer match what is saved on the quote (added or removed)
  public hasEquipmentOptionChanges(
    quote: Nullable<Quote>,
    equipmentList: Equipment[],
    rows: QuoteChargeListingEntity[]
  ): boolean {
    const equipmentIdsInRows = new Set(
      rows.filter(row => row.type === QuoteLineItemType.Equipment).map(row => row.equipment!.equipmentId)
    );

    return equipmentList.some(equipment => {
      if (!equipment.options?.length || !equipmentIdsInRows.has(equipment.equipmentId)) return false;

      const current = this.currentOptionLabels(rows, equipment);
      const saved = this.savedOptionLabels(quote, equipment);

      if (current.size !== saved.size) return true;

      return Array.from(current).some(label => !saved.has(label));
    });
  }

  private buildOptionNameLookup(list: Equipment[]): Map<string, Set<string>> {
    const map = new Map<string, Set<string>>();

    list.forEach(equipment => {
      if (Array.isArray(equipment.options) && equipment.options.length) {
        map.set(equipment.equipmentId, new Set(equipment.options.map(option => option.name)));
      }
    });

    return map;
  }

  private buildMissingEquipmentOptionRows(equipment: Equipment, quote: Nullable<Quote>): QuoteChargeListingEntity[] {
    if (!Array.isArray(equipment.options) || !equipment.options.length) return [];

    const bucket: ChargeBucketRef = { id: equipment.equipmentId, type: ChargeAttachType.Equipment };

    const existingLabels = new Set(
      (quote?.fees ?? []).filter(fee => fee.equipmentId === equipment.equipmentId).map(fee => fee.description)
    );

    return this.mapEquipmentInventoryOptionsToCharges(equipment.options)
      .filter(({ label }) => !existingLabels.has(label))
      .map(({ type, label, category, price = 0 }) =>
        this.chargeListingService.createChargeRowFromTemplate(bucket, {
          label,
          type: category,
          financeType: type,
          isEquipmentOption: true,
          price: { ...newChargeRow, wholesalePrice: price, retailPrice: price },
        })
      );
  }

  private toFeeListingRow(
    fee: Quote['fees'][number],
    voId: string,
    parentKindById: Map<string, ChargeParentKind>,
    optionNamesByEquipmentId: Map<string, Set<string>> = new Map()
  ): QuoteChargeListingEntity {
    const parentId = fee.parentId ?? null;
    const parentType: ChargeParentKind | undefined = parentId ? (parentKindById.get(parentId) ?? 'charge') : undefined;
    const isEquipmentOption = !!(
      fee.equipmentId && optionNamesByEquipmentId.get(fee.equipmentId)?.has(fee.description)
    );

    return {
      type: QuoteLineItemType.Invoice,
      equipment: null,
      charge: {
        parentId,
        parentType,
        isEquipmentOption,
        type: fee.type,
        amount: fee?.amount,
        cap: fee?.cap ?? null,
        id: fee.id ?? uuidv4(),
        label: fee.description,
        amountType: fee?.amountType,
        price: fee?.price ?? newChargeRow,
        isTaxable: fee?.isTaxable ?? undefined,
        financeType: chargeTypesByLabels[fee.description],
        isFromCommonRules: fee?.isFromCommonRules ?? false,
        customerVisibility: fee?.customerVisibility ?? undefined,
        groupBucket: {
          id: fee.equipmentId ?? voId,
          type: fee.equipmentId ? ChargeAttachType.Equipment : ChargeAttachType.Invoice,
        },
      },
    };
  }

  private buildParentKindLookup(equipmentRows: QuoteChargeListingEntity[]): Map<string, ChargeParentKind> {
    const map = new Map<string, ChargeParentKind>();

    equipmentRows.forEach(row => {
      if (row.type !== QuoteLineItemType.Equipment || !row.equipment) return;
      map.set(row.equipment.equipmentId, 'equipment');
    });

    return map;
  }

  public toQuoteVariables(input: QuoteChargeListingEntity[]): QuoteInput {
    const equipment = input
      .filter(row => row.type === QuoteLineItemType.Equipment)
      .map(row => this.toQuoteEquipmentEntry(row.equipment!));

    const fees = input
      .filter(row => row.type === QuoteLineItemType.Invoice)
      .map(row => this.toQuoteFeeEntry(row.charge!));

    return removeGraphQLMEtaFields({ ...this.emptyQuoteInput(), fees, equipment });
  }

  public toEquipmentQuoteVariables(equipment: EquipmentLike, quote: Nullable<Quote>, charges: Charge[]): QuoteInput {
    const equipmentEntry: QuoteEquipmentEntry = {
      attributes: [],
      externalId: '',
      cost: equipment.retailPrice,
      equipmentId: equipment.equipmentId,
      stockNumber: equipment.stockId ?? '',
      vin: equipment.serialNumberVin ?? '',
      price: equipment.price ?? { ...newChargeRow, retailPrice: equipment.retailPrice ?? 0 },
    };

    if (!quote) {
      return {
        ...this.emptyQuoteInput(),
        fees: charges.map(charge => this.toQuoteFeeForEquipment(charge, equipment.equipmentId)),
        equipment: [equipmentEntry],
      };
    }

    const otherFees = quote.fees.filter(fee => fee.equipmentId !== equipment.equipmentId);
    const newFees = charges.map(charge => this.toQuoteFeeForEquipment(charge, equipment.equipmentId));

    return {
      ...quote,
      fees: [...otherFees, ...newFees],
      equipment: quote.equipment.map(quoteEquipment =>
        equipment.equipmentId === quoteEquipment.equipmentId ? equipmentEntry : quoteEquipment
      ),
    };
  }

  private toQuoteEquipmentEntry(equipment: QuoteEquipmentWithCharge): QuoteEquipmentEntry {
    return {
      attributes: [],
      externalId: '',
      cost: equipment.price.retailPrice,
      equipmentId: equipment.equipmentId,
      stockNumber: equipment.stockId ?? '',
      vin: equipment.serialNumberVin ?? '',
      price: equipment.price ?? newChargeRow,
    };
  }

  private toQuoteFeeEntry(charge: Charge): QuoteInput['fees'][number] {
    return {
      id: charge.id,
      cap: charge.cap,
      type: charge.type,
      price: charge.price,
      description: charge.label,
      parentId: charge.parentId,
      amount: charge.price.total,
      isTaxable: charge.isTaxable,
      parentType: charge.parentType,
      amountType: charge.amountType ?? 'flat',
      isFromCommonRules: charge.isFromCommonRules,
      customerVisibility: charge.customerVisibility,
      isEquipmentLvl: charge.groupBucket.type === ChargeAttachType.Equipment,
      equipmentId: charge.groupBucket.type === ChargeAttachType.Equipment ? charge.groupBucket.id : null,
    };
  }

  private toQuoteFeeForEquipment(charge: Charge, equipmentId: string): QuoteInput['fees'][number] {
    return {
      equipmentId,
      type: charge.type,
      price: charge.price,
      amountType: 'flat',
      description: charge.label,
      amount: calculateTotal(charge.price),
    };
  }

  private emptyQuoteInput(): QuoteInput {
    return {
      term: null,
      fees: [],
      equipment: [],
      downPayment: null,
      interestRate: null,
      paymentAmount: null,
      contractStartDate: null,
      firstPaymentDueDate: null,
      rebates: [],
      attributes: [],
      cashBackAmounts: [],
      equipmentTradeIns: [],
      aftermarketProducts: [],
    };
  }

  public toInvoiceDTO({ equipment, fees }: QuoteInput): UpdateInvoiceVariables['fieldsObj'] {
    const equipmentCost = equipment.reduce((cost, entry) => cost + (entry.price?.retailPrice ?? 0), 0);

    const salesTaxOnInvoice = fees.reduce((total, fee) => total + calcTaxes(fee as unknown as Charge), 0);
    const freightLaborInstallation = fees.reduce((total, fee) => total + calcFees(fee as unknown as Charge), 0);

    return { equipmentCost, salesTaxOnInvoice, freightLaborInstallation };
  }

  public toEquipmentSyncDTO(equipmentForUpdate: QuoteEquipmentWithCharge) {
    const { price } = equipmentForUpdate;

    const result: EquipmentSyncDTO = {
      year: equipmentForUpdate.year,
      make: equipmentForUpdate.make,
      model: equipmentForUpdate.model,
      mileage: equipmentForUpdate.mileage,
      stockId: equipmentForUpdate.stockId,
      quantity: equipmentForUpdate.quantity,
      truckClass: equipmentForUpdate.class,
      newUsed: equipmentForUpdate.condition,
      wholesaleCost: price.wholesalePrice ?? 0,
      serialNumberVin: equipmentForUpdate?.vin?.trim(),
      totalWholesaleCost: equipmentForUpdate.wholeSaleCost,
      additionalDescription: equipmentForUpdate.description,
      retailPrice: equipmentCalc.calcRetailPrice(equipmentForUpdate),
    };

    return {
      ...result,
      discountValue: price.discount ?? 0,
      discountType: price.discountType ?? DiscountType.Flat,
      attachments: (equipmentForUpdate.attachments ?? []).map(attachment => this.toAttachmentSyncDTO(attachment)),
    };
  }

  public getChargesFromEquipment(bucket: ChargeBucketRef, equipmentList: Equipment[]): Nullable<ChargesByType> {
    if (bucket.type !== ChargeAttachType.Equipment) return null;

    const equipment = equipmentList.find(equipment => equipment.equipmentId === bucket.id);

    if (!equipment || !Array.isArray(equipment.options) || !equipment.options.length) return null;

    return {
      [ChargeType.EquipmentOptions]: this.mapEquipmentInventoryOptionsToCharges(equipment.options),
    };
  }

  private mapEquipmentInventoryOptionsToCharges(
    options: Required<Pick<Equipment, 'options'>>['options']
  ): ChargeDefinition[] {
    return options.map(option => ({
      type: option.code,
      label: option.name,
      price: option.cost,
      category: QuoteFeeType.Other,
    }));
  }
}

export const quoteMapper = new QuoteMapper();
